import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Card } from './Card'
import type { Severidad } from './tokens'

/**
 * Card con encabezado y pie. Es el contenedor de una tabla, un gráfico o una
 * lista: lo que necesita título propio pero está subordinado a una `Section`.
 *
 * El `h3` del panel pesa menos que el `h2` de su sección a propósito: con los
 * dos en semibold la jerarquía existe en el markup pero no se ve.
 */
export interface PanelProps {
  titulo?: string
  /** Bajada del panel, una línea. */
  subtitulo?: ReactNode
  /** Acciones a la derecha del encabezado. */
  acciones?: ReactNode
  /** Pie: totales, nota de fuente, "ver todo". */
  footer?: ReactNode
  /** `none` deja el cuerpo a sangre, para una tabla que llega hasta el borde. */
  padding?: 'none' | 'sm' | 'md'
  severidad?: Severidad
  elevada?: boolean
  className?: string
  children: ReactNode
}

const PADDING: Record<NonNullable<PanelProps['padding']>, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
}

export function Panel({
  titulo,
  subtitulo,
  acciones,
  footer,
  padding = 'md',
  severidad,
  elevada,
  className,
  children,
}: PanelProps) {
  const conEncabezado = titulo !== undefined || acciones !== undefined

  return (
    <Card
      padding="none"
      severidad={severidad}
      elevada={elevada}
      className={cx('overflow-hidden', className)}
    >
      {conEncabezado ? (
        <div
          data-testid="panel-head"
          className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-border bg-n100 px-3 py-2"
        >
          <div className="min-w-0">
            {titulo !== undefined ? (
              <h3 className="text-base font-medium tracking-normal text-text">
                {titulo}
              </h3>
            ) : null}
            {subtitulo !== undefined ? (
              <p className="mt-0.5 text-meta text-muted-strong">{subtitulo}</p>
            ) : null}
          </div>
          {acciones !== undefined ? (
            <div className="flex flex-wrap items-center gap-2">{acciones}</div>
          ) : null}
        </div>
      ) : null}

      <div className={cx('min-w-0', PADDING[padding])}>{children}</div>

      {footer !== undefined ? (
        <div
          data-testid="panel-foot"
          className="border-t border-border bg-n100 px-3 py-2 text-meta text-muted-strong"
        >
          {footer}
        </div>
      ) : null}
    </Card>
  )
}
